import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import GlobalSearchBox from '../GlobalSearchBox';
import { MOBILE_ISLAND_HOME_PATH, MOBILE_ISLAND_KEYBOARD_OPEN_INSET_PX, MOBILE_ISLAND_QUICK_LINK_HIGHLIGHT_MS, MOBILE_ISLAND_QUICK_LINKS_DIRECT_MAX } from './constants';
import MobileIslandDockDefaultQuickLinks from './MobileIslandDockDefaultQuickLinks';
import MobileIslandDockLayout from './MobileIslandDockLayout';
import MobileIslandActionIsland from './MobileIslandActionIsland';
import WorkbenchIconAction from '../common/WorkbenchIconAction';
import { useMobileKeyboardInset } from '../../hooks/useMobileKeyboardInset';
import { useMobileWorkbenchSheetOpen } from '../../hooks/useMobileWorkbenchSheetOpen';
import { useSearchContext } from '../../providers/SearchProvider';
import QuickLinks from '../../screens/dashboard/QuickLinks';
import { useQuickLinkButtons } from '../../screens/dashboard/QuickOperateButton';
import {
	DASHBOARD_HOME_SCROLL_TOP_THRESHOLD,
	getDashboardHomeMobileScrollEl,
	isDashboardHomeScrolled,
	scrollDashboardHomeToTop,
} from '../../utils/dashboardHomeMobileScroll';
import { useMobileIslandDockBodyClasses } from './useMobileIslandDockBodyClasses';
import { useMobileIslandSearchCollapseOnScroll } from './useMobileIslandSearchCollapseOnScroll';
import { hasPermission, PERM } from '../../utils/accessPermissions';
import { consumeMobileLastWorkbenchPath, mobileWorkbenchPathsMatch } from '../../utils/mobileIslandLastWorkbenchPath';

/** 默认三岛：Home | 快速链接 | 全局搜索（仅首页） */
const MobileIslandDockDefault = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { clearSearchResults, showSearchCard, searchKeyword, setShowSearchCardState } = useSearchContext();
	const loadBtn = useQuickLinkButtons();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [searchExpanded, setSearchExpanded] = useState(false);
	const [inputFocused, setInputFocused] = useState(false);
	const [isHomeScrolled, setIsHomeScrolled] = useState(false);
	const [highlightPath, setHighlightPath] = useState(null);
	const sheetSwipeRef = useRef(null);
	const isOnDashboardHome = pathname === MOBILE_ISLAND_HOME_PATH;

	const searchExpandedActive = isOnDashboardHome && searchExpanded;
	const keyboardInsetPx = useMobileKeyboardInset(searchExpandedActive && inputFocused);
	const keyboardOpen = keyboardInsetPx >= MOBILE_ISLAND_KEYBOARD_OPEN_INSET_PX;

	useMobileIslandDockBodyClasses({
		searchExpanded: searchExpandedActive,
		searchFocused: keyboardOpen,
	});
	useMobileWorkbenchSheetOpen(drawerOpen);

	const { directLinks, showMore } = useMemo(() => {
		if (!loadBtn?.length) {
			return { directLinks: [], showMore: false };
		}
		if (loadBtn.length <= MOBILE_ISLAND_QUICK_LINKS_DIRECT_MAX) {
			return { directLinks: loadBtn, showMore: false };
		}
		return { directLinks: loadBtn.slice(0, MOBILE_ISLAND_QUICK_LINKS_DIRECT_MAX), showMore: true };
	}, [loadBtn]);

	useEffect(() => {
		if (!isOnDashboardHome) {
			return undefined;
		}

		const lastPath = consumeMobileLastWorkbenchPath();
		if (!lastPath) {
			setHighlightPath(null);
			return undefined;
		}

		setHighlightPath(lastPath);
		const timer = window.setTimeout(() => setHighlightPath(null), MOBILE_ISLAND_QUICK_LINK_HIGHLIGHT_MS);
		return () => window.clearTimeout(timer);
	}, [isOnDashboardHome]);

	const highlightMore = useMemo(() => {
		if (!highlightPath || !showMore) {
			return false;
		}
		if (directLinks.some((button) => mobileWorkbenchPathsMatch(button.link, highlightPath))) {
			return false;
		}
		return loadBtn.some((button) => mobileWorkbenchPathsMatch(button.link, highlightPath));
	}, [directLinks, highlightPath, loadBtn, showMore]);

	const clearQuickLinkHighlight = useCallback(() => {
		setHighlightPath(null);
	}, []);

	useEffect(() => {
		setSearchExpanded(false);
		setInputFocused(false);
		setDrawerOpen(false);
		setShowSearchCardState(false);
	}, [pathname, setShowSearchCardState]);

	useEffect(() => {
		if (!isOnDashboardHome) {
			setIsHomeScrolled(false);
			return undefined;
		}

		const scrollEl = getDashboardHomeMobileScrollEl();
		if (!scrollEl) {
			return undefined;
		}

		const update = () => {
			setIsHomeScrolled(scrollEl.scrollTop > DASHBOARD_HOME_SCROLL_TOP_THRESHOLD);
		};

		update();
		scrollEl.addEventListener('scroll', update, { passive: true });
		return () => scrollEl.removeEventListener('scroll', update);
	}, [isOnDashboardHome, showSearchCard]);

	const handleSheetTouchStart = useCallback((e) => {
		if (e.touches.length !== 1) return;
		sheetSwipeRef.current = { startY: e.touches[0].clientY };
	}, []);

	const handleSheetTouchMove = useCallback((e) => {
		const swipe = sheetSwipeRef.current;
		if (!swipe || e.touches.length !== 1) return;
		if (e.touches[0].clientY - swipe.startY > 64) {
			setDrawerOpen(false);
			sheetSwipeRef.current = null;
		}
	}, []);

	const handleSheetTouchEnd = useCallback(() => {
		sheetSwipeRef.current = null;
	}, []);

	const handleCloseSearch = useCallback(() => {
		setSearchExpanded(false);
		setInputFocused(false);
		if (!searchKeyword?.trim()) {
			setShowSearchCardState(false);
		}
	}, [searchKeyword, setShowSearchCardState]);

	const handleOpenSearch = useCallback(() => {
		if (!isOnDashboardHome) {
			return;
		}
		setShowSearchCardState(true);
		setSearchExpanded(true);
		setDrawerOpen(false);
	}, [isOnDashboardHome, setShowSearchCardState]);

	const handleCollapseSearch = useCallback(() => {
		requestAnimationFrame(() => {
			setSearchExpanded(false);
			setInputFocused(false);
		});
	}, []);

	useMobileIslandSearchCollapseOnScroll(searchExpandedActive, handleCollapseSearch, inputFocused);

	const homeActionLabel = useMemo(() => {
		if (isOnDashboardHome && showSearchCard) {
			return '返回首页';
		}
		if (!isOnDashboardHome) {
			return '返回首页';
		}
		if (isHomeScrolled) {
			return '回到顶部';
		}
		return '首页';
	}, [isHomeScrolled, isOnDashboardHome, showSearchCard]);

	const handleHomeClick = useCallback(() => {
		setSearchExpanded(false);
		setInputFocused(false);
		setDrawerOpen(false);

		if (isOnDashboardHome && showSearchCard) {
			clearSearchResults();
			return;
		}

		if (isOnDashboardHome && isDashboardHomeScrolled()) {
			scrollDashboardHomeToTop();
			return;
		}

		if (!isOnDashboardHome) {
			navigate(MOBILE_ISLAND_HOME_PATH);
		}
	}, [clearSearchResults, isOnDashboardHome, navigate, showSearchCard]);

	const handleOpenDrawer = useCallback(() => {
		setDrawerOpen(true);
		setSearchExpanded(false);
		setInputFocused(false);
	}, []);

	const quickLinksMiddle =
		loadBtn?.length > 0 ? (
			<MobileIslandDockDefaultQuickLinks
				directLinks={directLinks}
				showMore={showMore}
				drawerOpen={drawerOpen}
				highlightPath={highlightPath}
				highlightMore={highlightMore}
				onOpenDrawer={handleOpenDrawer}
				onCloseSearch={handleCloseSearch}
				onQuickLinkNavigate={clearQuickLinkHighlight}
			/>
		) : null;

	const globalSearchSlot = useMemo(() => {
		const canSearch = hasPermission(userInfo, PERM.MENU_DASHBOARD_SEARCH);
		if (!isOnDashboardHome || !canSearch) {
			return null;
		}

		if (searchExpanded) {
			return (
				<MobileIslandActionIsland className='app-mobile-action-island--search-field' ariaLabel='搜索'>
					<GlobalSearchBox
						variant='island'
						autoFocus
						placeholder='搜索订单、客户、产品...'
						onDismiss={handleCloseSearch}
						onFocusStateChange={setInputFocused}
					/>
				</MobileIslandActionIsland>
			);
		}

		return (
			<MobileIslandActionIsland single ariaLabel='搜索'>
				<WorkbenchIconAction
					className='app-mobile-action-island__search'
					icon='search'
					onClick={handleOpenSearch}
					active={Boolean(searchKeyword?.trim()) || showSearchCard}
					title='搜索'
					ariaLabel='搜索'
					ariaExpanded={searchExpanded}
				/>
			</MobileIslandActionIsland>
		);
	}, [handleCloseSearch, handleOpenSearch, isOnDashboardHome, searchExpanded, searchKeyword, showSearchCard, userInfo]);

	return (
		<>
			<MobileIslandDockLayout
				searchExpanded={searchExpandedActive}
				searchFocused={keyboardOpen}
				middleAriaLabel='快速入口'
				lead={
					<MobileIslandActionIsland single ariaLabel={homeActionLabel}>
						<WorkbenchIconAction
							className='app-mobile-action-island__home'
							icon='home'
							onClick={handleHomeClick}
							title={homeActionLabel}
							ariaLabel={homeActionLabel}
						/>
					</MobileIslandActionIsland>
				}
				middle={quickLinksMiddle}
				search={globalSearchSlot}
			/>

			{drawerOpen ? (
				<button
					type='button'
					className='dashboard-quick-links-sheet-capture'
					aria-label='关闭快速入口'
					onClick={() => setDrawerOpen(false)}
				/>
			) : null}

			{showMore ? (
				<Offcanvas
					show={drawerOpen}
					onHide={() => setDrawerOpen(false)}
					placement='bottom'
					backdrop={false}
					scroll
					className='dashboard-quick-links-offcanvas dashboard-quick-links-sheet'
				>
					<Offcanvas.Header
						className='dashboard-quick-links-sheet__header border-bottom flex-shrink-0'
						onTouchStart={handleSheetTouchStart}
						onTouchMove={handleSheetTouchMove}
						onTouchEnd={handleSheetTouchEnd}
						onTouchCancel={handleSheetTouchEnd}
					>
						<Offcanvas.Title className='fs-8 mb-0'>从这里开始您的工作</Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body className='dashboard-quick-links-offcanvas__body p-0'>
						<QuickLinks variant='drawer' onLinkNavigate={() => setDrawerOpen(false)} />
					</Offcanvas.Body>
				</Offcanvas>
			) : null}
		</>
	);
};

export default MobileIslandDockDefault;
